import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { ErrorBoundary } from './components/ErrorBoundary';import BackToTop from './components/BackToTop';
import './index.css';
import './pages/Responsive.css';

// Lazy load all pages — reduces initial bundle size significantly
const Home          = lazy(() => import('./pages/Home'));
const Products      = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart          = lazy(() => import('./pages/Cart'));
const Login         = lazy(() => import('./pages/Login'));
const Checkout      = lazy(() => import('./pages/Checkout'));
const Orders        = lazy(() => import('./pages/Orders'));
const OrderDetail   = lazy(() => import('./pages/OrderDetail'));
const Admin         = lazy(() => import('./pages/Admin'));
const Profile       = lazy(() => import('./pages/Profile'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const RefundPolicy    = lazy(() => import('./pages/RefundPolicy'));
const PrivacyPolicy   = lazy(() => import('./pages/PrivacyPolicy'));
const NotFound        = lazy(() => import('./pages/NotFound'));

const PageLoader = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#1a202c', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const PrivateRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  return user?.role === 'admin' ? children : <Navigate to="/" />;
};

function AppContent() {
  return (
    <Router>
      <Navbar />
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/"             element={<Home />} />
            <Route path="/products"     element={<Products />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/cart"         element={<Cart />} />
            <Route path="/login"        element={<Login />} />
            <Route path="/register"     element={<Login />} />
            <Route path="/checkout"     element={<PrivateRoute><Checkout /></PrivateRoute>} />
            <Route path="/orders"       element={<PrivateRoute><Orders /></PrivateRoute>} />
            <Route path="/orders/:id"   element={<PrivateRoute><OrderDetail /></PrivateRoute>} />
            <Route path="/profile"      element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/admin"        element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="/terms"        element={<TermsConditions />} />
            <Route path="/refund-policy"  element={<RefundPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="*"             element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      <Footer />
      {/* Floating WhatsApp Button */}
      <a
        href="https://api.whatsapp.com/send/?phone=918467025204"
        target="_blank"
        rel="noreferrer"
        style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          background: '#25D366', borderRadius: '50%', width: '56px', height: '56px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(37,211,102,0.4)', textDecoration: 'none', transition: 'transform 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        title="Chat on WhatsApp"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32" fill="white">
          <path d="M16 2C8.268 2 2 8.268 2 16c0 2.494.655 4.838 1.8 6.872L2 30l7.338-1.773A13.94 13.94 0 0016 30c7.732 0 14-6.268 14-14S23.732 2 16 2zm0 25.5a11.45 11.45 0 01-5.833-1.6l-.417-.25-4.35 1.05 1.083-4.233-.275-.433A11.46 11.46 0 014.5 16C4.5 9.649 9.649 4.5 16 4.5S27.5 9.649 27.5 16 22.351 27.5 16 27.5zm6.29-8.617c-.345-.172-2.04-1.006-2.355-1.12-.316-.115-.546-.172-.776.172-.23.345-.89 1.12-1.09 1.35-.2.23-.4.258-.746.086-.345-.172-1.457-.537-2.775-1.713-1.026-.916-1.718-2.047-1.919-2.392-.2-.345-.021-.531.15-.703.155-.155.345-.4.517-.6.172-.2.23-.345.345-.575.115-.23.057-.43-.029-.603-.086-.172-.776-1.87-1.063-2.56-.28-.672-.564-.58-.776-.59l-.66-.011c-.23 0-.603.086-.918.43-.316.345-1.205 1.178-1.205 2.872s1.233 3.33 1.405 3.56c.172.23 2.428 3.707 5.882 5.198.823.355 1.465.567 1.966.726.826.263 1.578.226 2.172.137.662-.099 2.04-.833 2.327-1.638.287-.804.287-1.493.2-1.638-.086-.144-.316-.23-.66-.4z"/>
        </svg>
      </a>
      <BackToTop />
      <Toaster
        position="top-right"
        toastOptions={{
          style: { background: '#1F2937', color: '#F9FAFB', border: '1px solid #374151', borderRadius: '10px', fontFamily: 'DM Sans, sans-serif' },
          success: { iconTheme: { primary: '#00E676', secondary: '#1F2937' } },
          error:   { iconTheme: { primary: '#FF4757', secondary: '#1F2937' } }
        }}
      />
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <AppContent />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
